import { createFileRoute, notFound } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { getCalculator } from "@/lib/calculators";
import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useT } from "@/lib/i18n";
import { ShareButton } from "@/components/calculator/ShareButton";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/$slug")({
  loader: ({ params }) => {
    const calc = getCalculator(params.slug);
    if (!calc) throw notFound();
    return { calc };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { calc } = loaderData;
    const title = `${calc.name.en} — Bangla Calculator`;
    const seo = buildSeo({
      title,
      description: calc.description.en,
      path: `/${calc.slug}`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: calc.name.en, path: `/${calc.slug}` },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CalculatorPage,
  notFoundComponent: () => (
    <div className="px-4 py-16 text-center">
      <h1 className="text-3xl font-bold">Calculator not found</h1>
      <p className="mt-2 text-muted-foreground">This calculator doesn't exist yet.</p>
    </div>
  ),
});

function CalculatorPage() {
  const { calc } = Route.useLoaderData();
  const { t, lang } = useT();

  return (
    <CalculatorLayout
      slug={calc.slug}
      title={calc.name[lang]}
      description={calc.description[lang]}
      iconName={calc.icon}
    >
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
          <Sparkles size={28} />
        </span>
        <div className="rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent">
          {t("common.comingSoon")}
        </div>
        <p className={`max-w-md text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
          {t("common.placeholderBody")}
        </p>
        <div className="mt-4">
          <ShareButton title={calc.name[lang]} />
        </div>
      </div>
    </CalculatorLayout>
  );
}
